import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import {
  emptyMessageListCriteriaForm,
  messageListCriteriaFromForm,
  MessageListCriteriaFields,
  type MessageListCriteriaForm,
  validateMessageListCriteriaForm,
} from '@/components/messaging/message-list-criteria-fields';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { contactReadiness, memberHasEmail, memberHasPhone } from '@/lib/messaging-contact';

interface ListCriteria extends Record<string, unknown> {}

interface MemberSearchRow {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

interface SelectedMember {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  memberType: 'first_timer' | 'visitor' | 'member';
}

interface CreateListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateListDialog = ({ open, onOpenChange }: CreateListDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tPeople } = useTranslation('people');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'fixed' | 'dynamic'>('dynamic');
  const [criteriaForm, setCriteriaForm] = useState<MessageListCriteriaForm>(
    emptyMessageListCriteriaForm(),
  );
  const [selectedMembers, setSelectedMembers] = useState<SelectedMember[]>([]);
  const [memberQuery, setMemberQuery] = useState('');

  const departments = useQuery({
    queryKey: ['messaging', 'departments'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments?pageSize=100'),
    enabled: open && kind === 'dynamic',
  });

  const groups = useQuery({
    queryKey: ['messaging', 'groups'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/groups?pageSize=100'),
    enabled: open && kind === 'dynamic',
  });

  const members = useQuery({
    queryKey: ['messaging', 'list-member-search', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberSearchRow[] }>(
        `/people/members?pageSize=50&status=active${
          memberQuery ? `&q=${encodeURIComponent(memberQuery)}` : ''
        }`,
      ),
    enabled: open && kind === 'fixed',
  });

  const criteriaValidation = validateMessageListCriteriaForm(criteriaForm);
  const criteriaInvalid = criteriaValidation.invalid;

  const buildCriteria = (): ListCriteria | null => {
    if (kind !== 'dynamic') return null;
    if (criteriaInvalid) return null;
    const criteria = messageListCriteriaFromForm(criteriaForm);
    return Object.keys(criteria).length ? criteria : null;
  };

  const criteria = buildCriteria();
  const preview = useQuery({
    queryKey: ['messaging', 'list-preview', kind, criteriaForm],
    queryFn: () =>
      api.post<{
        totalMembers: number;
        withPhone: number;
        withEmail: number;
        missingBoth: number;
      }>('/messaging/lists/preview', {
        kind,
        criteria,
      }),
    enabled:
      open &&
      kind === 'dynamic' &&
      !criteriaInvalid &&
      Boolean(criteria && Object.keys(criteria).length),
  });

  const reset = () => {
    setName('');
    setDescription('');
    setKind('dynamic');
    setCriteriaForm(emptyMessageListCriteriaForm());
    setSelectedMembers([]);
    setMemberQuery('');
  };

  const create = useMutation({
    mutationFn: () =>
      api.post('/messaging/lists', {
        name,
        description: description || null,
        kind,
        criteria: buildCriteria(),
        memberIds: kind === 'fixed' ? selectedMembers.map((row) => row.id) : undefined,
      }),
    onSuccess: () => {
      toast.success(t('lists.saved'));
      onOpenChange(false);
      reset();
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'lists'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const memberOptions =
    members.data?.data
      .filter((row) => !selectedMembers.some((selected) => selected.id === row.id))
      .map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })) ?? [];

  const memberName = (row: SelectedMember) =>
    `${row.firstName} ${row.lastName}`.trim();

  const fixedContact = contactReadiness(selectedMembers);

  const selectedColumns: DataTableColumn<SelectedMember>[] = [
    {
      id: 'name',
      header: t('lists.selectedColumns.name'),
      cell: (row) => <MemberNameLink memberId={row.id} name={memberName(row)} />,
    },
    {
      id: 'type',
      header: t('lists.selectedColumns.type'),
      cell: (row) => tPeople(`members.type.${row.memberType}`),
      hideBelowMd: true,
    },
    {
      id: 'phone',
      header: t('lists.selectedColumns.phone'),
      cell: (row) =>
        memberHasPhone(row) ? (
          row.phone
        ) : (
          <span className="text-fg-secondary">—</span>
        ),
      hideBelowMd: true,
    },
    {
      id: 'email',
      header: t('lists.selectedColumns.email'),
      cell: (row) =>
        memberHasEmail(row) ? (
          row.email
        ) : (
          <span className="text-fg-secondary">—</span>
        ),
      hideBelowMd: true,
    },
  ];

  const canSubmit =
    name.trim().length > 0 &&
    (kind === 'dynamic'
      ? !criteriaInvalid && Boolean(criteria && Object.keys(criteria).length)
      : selectedMembers.length > 0) &&
    !create.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="xl"
        closeLabel={tCommon('action.close')}
        className="overflow-visible"
      >
        <DialogHeader>
          <DialogTitle>{t('lists.create')}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[75vh] pr-2">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="list-name">{t('lists.name')}</Label>
                <Input id="list-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label>{t('lists.kind')}</Label>
                <Listbox
                  value={kind}
                  onChange={(value) => {
                    setKind((value as 'fixed' | 'dynamic') ?? 'dynamic');
                    setSelectedMembers([]);
                  }}
                  options={[
                    { value: 'dynamic', label: t('lists.kindDynamic') },
                    { value: 'fixed', label: t('lists.kindFixed') },
                  ]}
                  popoverPortal="dialog"
                />
                <p className="mt-1 text-caption text-fg-secondary">
                  {kind === 'fixed' ? t('lists.kindFixedHelp') : t('lists.kindDynamicHelp')}
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="list-description">{t('lists.description')}</Label>
              <Textarea
                id="list-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            {kind === 'dynamic' ? (
              <>
                <MessageListCriteriaFields
                  value={criteriaForm}
                  onChange={setCriteriaForm}
                  departments={departments.data?.data ?? []}
                  groups={groups.data?.data ?? []}
                />
                {preview.data ? (
                  <div className="space-y-1 text-body-sm text-fg-secondary">
                    <p>{t('lists.previewCount', { count: preview.data.totalMembers })}</p>
                    {preview.data.totalMembers > 0 ? (
                      <p>
                        {t('lists.contactReadiness', {
                          withPhone: preview.data.withPhone,
                        })}
                      </p>
                    ) : null}
                    {preview.data.withPhone < preview.data.totalMembers ? (
                      <p className="text-warning" role="status">
                        {t('lists.missingPhoneWarning', {
                          count: preview.data.totalMembers - preview.data.withPhone,
                        })}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>{t('lists.addMembers')}</Label>
                  <EntityCombobox
                    value={null}
                    onChange={(id) => {
                      if (!id) return;
                      const row = members.data?.data.find((member) => member.id === id);
                      if (!row) return;
                      setSelectedMembers((current) =>
                        current.some((member) => member.id === id)
                          ? current
                          : [
                              ...current,
                              {
                                id: row.id,
                                firstName: row.firstName,
                                lastName: row.lastName,
                                phone: row.phone ?? null,
                                email: row.email ?? null,
                                memberType: row.memberType,
                              },
                            ],
                      );
                    }}
                    options={memberOptions}
                    onQueryChange={setMemberQuery}
                    loading={members.isPending}
                    placeholder={t('lists.searchMembers')}
                    popoverPortal="dialog"
                    clearLabel={tCommon('action.clearSelection')}
                  />
                </div>
                <div>
                  <Label>{t('lists.selectedMembers')}</Label>
                  <div className="mt-2">
                    <DataTable
                      rows={selectedMembers}
                      columns={selectedColumns}
                      rowKey={(row) => row.id}
                      empty={
                        <p className="text-body-sm text-fg-secondary">
                          {t('lists.noMembersSelected')}
                        </p>
                      }
                      actionsHeader={tCommon('table.actions')}
                      rowActions={(row) => (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setSelectedMembers((current) =>
                              current.filter((member) => member.id !== row.id),
                            )
                          }
                        >
                          {tCommon('action.remove')}
                        </Button>
                      )}
                    />
                  </div>
                  <p className="mt-1 text-caption text-fg-secondary">
                    {t('lists.memberCount', { count: selectedMembers.length })}
                  </p>
                  {selectedMembers.length > 0 ? (
                    <p className="mt-1 text-caption text-fg-secondary">
                      {t('lists.contactReadiness', {
                        withPhone: fixedContact.withPhone,
                      })}
                    </p>
                  ) : null}
                  {fixedContact.missingPhone > 0 ? (
                    <p className="mt-1 text-body-sm text-warning" role="status">
                      {t('lists.missingPhoneWarning', { count: fixedContact.missingPhone })}
                    </p>
                  ) : null}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button disabled={!canSubmit} onClick={() => create.mutate()}>
                {t('lists.create')}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
